import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl,
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { ShopViewModel } from "../../../Types/ExportTypes";
import { CountryAutoComplete, AccountAutoComplete, TemplateCustomerAutoComplete, LocationAutoComplete, CustomerPriceGroupAutoComplete, Dimension1AutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import ImageBox from "../../../components/Image/ImageBox";
import DefaultActions from "../../../components/Card/DefaultActions";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const LocationEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { shopNo = "" } = params || {};
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(ShopViewModel) | null>(null);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (shopNo) {
      setIsLoading(true);
      authService
        .get<ShopViewModel>(`/settingsApi/GetShop?shopNo=${shopNo}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")} : ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultModel: ShopViewModel & { originalValues: Partial<ShopViewModel>; hasChanges: boolean } = {
        unit: "",
        shopNo: "",
        shopName: "",
        isCreating: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultModel.originalValues = { ...defaultModel };
      setViewModel(defaultModel);
    }
  }, [shopNo, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateShop()) return; // Prevent save if validation fails
    var newItem = viewModel.isCreating ?? false;
    setIsLoading(true);
    try {
      const response = await authService.post<ShopViewModel>("/settingsApi/CreateOrUpdateShop", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (pendingImage) {
        await uploadImage(pendingImage, response.shopNo);
        setPendingImage(null);
      }
      if (newItem) {
        navigate({ path: "/settings/shop/edit", params: { shopNo: response.shopNo } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof ShopViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as ShopViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const handleCopy = async () => {
    var newItem = viewModel?.isCreating ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryEntity"), "warning");
      return;
    }
    if (viewModel?.hasChanges) showHasChangesError();
    setIsLoading(true);
    try {
      var response = await authService.post<ShopViewModel>(`/settingsApi/CopyShop`, viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.entityCopied"), "success");

      navigate({ path: "/settings/shop/edit", params: { shopNo: response.shopNo } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.copyFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.isCreating ?? false;
    if (newItem) {
      navigate({ path: "/settings/shop/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteShop`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/shop/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const validateShop = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.shopName || viewModel.shopName.trim() === "") {
      showSnackbar(t("help.nameRequired"), "warning");
      return false;
    }


    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (viewModel?.isCreating == true) {
      setPendingImage(file);
      setPendingImageUrl(URL.createObjectURL(file));
    } else {
      uploadImage(file, viewModel?.shopNo);
    }
  };

  const uploadImage = async (file: File, shopNo?: string | null) => {
    try {
      if (!shopNo) return;
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("shopNo", shopNo);

      const response = await authService.post<{ url: string }>("/settingsApi/UploadImageForShop", formData);
      if (response?.url) {
        setViewModel((prev) => ({ ...prev, cdnImageLink: response.url }));
        showSnackbar(t("dialog.imageUploaded"), "success");
      } else {
        throw new Error("Failed to upload image.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showSnackbar(t("dialog.imageUploadError"), "error");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.shop")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onCopy={handleCopy}
            onDelete={handleDelete}

          />
        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.number")}
                  value={viewModel.shopNo || ""}
                  //onChange={(e) => handleChange("shopNo", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={true}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} >


                <FormControl fullWidth margin="normal">
                  <LocationAutoComplete
                    key={viewModel.locationCode || undefined}
                    valueId={viewModel.locationCode || null}
                    onChange={(selected) => handleChange("locationCode", selected?.value || "")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <TemplateCustomerAutoComplete
                    key={viewModel.customerTemplateNo || undefined}
                    valueId={viewModel.customerTemplateNo || null}
                    onChange={(selected) => handleChange("customerTemplateNo", selected?.value || "")}
                    label={t("common.templateCustomerB2C")}

                  />

                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <CustomerPriceGroupAutoComplete
                    key={viewModel.customerPriceGroup || undefined}
                    valueId={viewModel.customerPriceGroup || null}
                    onChange={(selected) => handleChange("customerPriceGroup", selected?.value || "")}
                    infoText={t("help.priceGroupShop")}
                  />

                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <Dimension1AutoComplete
                    key={viewModel.globalDimension1Code || null}
                    valueId={viewModel.globalDimension1Code || null}
                    onChange={(selected) => handleChange("globalDimension1Code", selected?.value || "")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.name")}
                  value={viewModel.shopName || ""}
                  onChange={(e) => handleChange("shopName", e.target.value)}
                  fullWidth
                  margin="normal"
                  error={!viewModel.shopName || viewModel.shopName.trim() === ""}
                  helperText={!viewModel.shopName || viewModel.shopName.trim() === "" ? t("help.nameRequired") : ""}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.address")}
                  value={viewModel.shopAdress || ""}
                  onChange={(e) => handleChange("shopAdress", e.target.value)}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.address2")}
                  value={viewModel.shopAddress2 || ""}
                  onChange={(e) => handleChange("shopAddress2", e.target.value)}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("common.postCode")}
                  value={viewModel.postCode || ""}
                  onChange={(e) => handleChange("postCode", e.target.value)}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("common.city")}
                  value={viewModel.city || ""}
                  onChange={(e) => handleChange("city", e.target.value)}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t("common.contact")}
                  value={viewModel.contact || ""}
                  onChange={(e) => handleChange("contact", e.target.value)}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.email")}
                  value={viewModel.eMail || ""}
                  onChange={(e) => handleChange("eMail", e.target.value)}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {/* Current or Temporary Image Preview */}
                  <ImageBox
                    imageUrl={pendingImage ? pendingImageUrl ?? "" : viewModel.cdnImageLink ?? ""}
                    altText={viewModel.shopName ?? ""}
                    fallbackText={t("common.noImage")}
                    width="300px"
                  />

                  {/* Image Upload Input */}
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ textTransform: "none", maxWidth: "250px", mt: 2 }}
                  >
                    {t("common.uploadImage")}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                </Box>

              </Grid>

            </Grid>


          </>
        )}



        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.cancel")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>

    </>
  );
};

export default LocationEditPage;
